import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SpotBadge } from './CapChecker';
import FoundingMemberCounter from './FoundingMemberCounter';
import { formatCategory } from '../constants/categories';
import { CATEGORY_GROUPS } from '../constants/categoryGroups';
import { BATON_ROUGE_METRO, STATEWIDE_CAP_CITIES } from '../constants/metros';
import { classifyFootprintError, laneReasonText, failingPairLabel, FootprintError } from '../utils/footprintErrors';
import { checkoutConflictMessage } from '../utils/checkoutErrors';

// Spot-availability category selector renders the full canonical category
// list — CATEGORY_OPTIONS in constants/categories.ts, grouped for display
// via constants/categoryGroups.ts. Sync-required: those lists must match
// backend src/categories.ts. If you change the category set, change it there.

const Pricing: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [userCity, setUserCity] = useState('Baton Rouge');
  const [userCategory, setUserCategory] = useState('roofing');
  // F4-a: checkout footprint error notice (plan-limit / lane). Error-path only
  // — absent on normal render, no pricing content changes.
  const [checkoutNotice, setCheckoutNotice] = useState<FootprintError>(null);
  const token = localStorage.getItem('token');

  // C2: tier-aware funnel. ?tier=pro|premium highlights the matching
  // card and changes its CTA text. Customers see the page unchanged
  // (tier funnel is for businesses only).
  const [searchParams] = useSearchParams();
  const highlightedTier = searchParams.get('tier');
  const userJson = localStorage.getItem('user');
  let isCustomer = false;
  if (userJson) {
    try { isCustomer = JSON.parse(userJson).user_type === 'customer'; } catch { /* ignore */ }
  }
  const isHighlighted = (planTier: 'pro' | 'premium') =>
    !isCustomer && highlightedTier === planTier;

  // Single-use latch: auto-checkout may fire AT MOST ONCE per mount. Set the
  // instant we initiate, checked before firing, so re-renders / state changes
  // (and a 401/403 that resets `loading`) can never relaunch checkout.
  const autoCheckoutFired = useRef(false);

  const handleUpgrade = async (tier: 'pro' | 'premium') => {
    if (!token) { window.location.href = `/register?type=business&tier=${tier}`; return; }
    setLoading(tier);
    setCheckoutNotice(null);
    try {
      const response = await fetch('https://quotebid-backend-production.up.railway.app/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ tier })
      });
      const data = await response.json();
      if (data.url) { window.location.href = data.url; return; }
      // Honor the footprint contracts: 400 FOOTPRINT_EXCEEDS_PLAN -> plan-limit
      // notice; 409 failing_pairs -> lane list. Shape a synthetic axios-style
      // error so the shared classifier handles a fetch response identically.
      const footprint = classifyFootprintError({ response: { status: response.status, data } });
      // 409 ACTIVE_SUBSCRIPTION_EXISTS / CHECKOUT_IN_PROGRESS: show the backend's
      // message verbatim in the same inline notice as the plan-limit error.
      const conflictMessage = checkoutConflictMessage(data);
      if (footprint) { setCheckoutNotice(footprint); }
      else if (conflictMessage) { setCheckoutNotice({ kind: 'plan-limit', message: conflictMessage }); }
      else { alert('Error creating checkout session'); }
    } catch (error) { alert('Error connecting to server'); }
    finally { setLoading(null); }
  };

  // Gated auto-checkout: only for an intentional paid-flow URL
  // (/pricing?tier=pro|premium&checkout=1) arrived at by a logged-in business
  // user. Reuses the exact handleUpgrade path the manual button uses — no second
  // checkout code path. Fires once (ref latch); on 401/403 handleUpgrade surfaces
  // the error and we do NOT retry or loop.
  useEffect(() => {
    if (autoCheckoutFired.current) return;
    const checkoutParam = searchParams.get('checkout');
    const tierIsPaid = highlightedTier === 'pro' || highlightedTier === 'premium';
    if (
      token &&                  // (1) token present (validity unknown until the API answers)
      !isCustomer &&            // (2) business user, not customer
      tierIsPaid &&             // (3) URL tier ∈ {pro, premium}
      checkoutParam === '1' &&  // (4) explicit checkout=1
      loading === null          // (5) no checkout already in flight
    ) {
      autoCheckoutFired.current = true; // latch BEFORE firing
      handleUpgrade(highlightedTier as 'pro' | 'premium');
    }
    // Mount-only + ref-latched: intentionally not re-run on dependency changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Check = () => <span style={{ color: '#10b981', fontWeight: 700 }}>Yes</span>;
  const Cross = () => <span style={{ color: '#cbd5e1' }}>No</span>;

  return (
    <div style={styles.root}>
      {checkoutNotice?.kind === 'plan-limit' && (
        <div style={styles.checkoutPlanNotice}>{checkoutNotice.message}</div>
      )}
      {checkoutNotice?.kind === 'lanes' && checkoutNotice.pairs.length > 0 && (
        <div style={styles.checkoutLaneNotice}>
          <div style={styles.checkoutLaneTitle}>Some of your lanes aren't available:</div>
          <ul style={styles.checkoutLaneList}>
            {checkoutNotice.pairs.map((p, i) => (
              <li key={i} style={styles.checkoutLaneItem}><strong>{failingPairLabel(p, formatCategory)}</strong> — {laneReasonText(p.reason)}</li>
            ))}
          </ul>
        </div>
      )}
      <section style={styles.hero}>
        <div style={styles.heroBadge}>Founding Member pricing — first 24 paid Founding Members only</div>
        <FoundingMemberCounter tone="onDark" />
        <h1 style={styles.heroTitle}>Plans That Grow<br /><span style={styles.heroAccent}>With Your Business</span></h1>
        <p style={styles.heroSubtitle}>No pay-per-lead fees. No contracts. Cancel anytime.<br />The earlier you see a job, the better your chance of winning it.</p>
      </section>

      <section style={styles.timingSection}>
        <div style={styles.sectionContainer}>
          <div style={styles.sectionLabel}>HOW THE SYSTEM WORKS</div>
          <h2 style={styles.sectionTitle}>The Earlier You Know, The More You Win</h2>
          <p style={styles.timingSubtitle}>When a customer posts a request, QuoteBid notifies businesses in tiers. Being first means submitting the strongest quote before the competition even knows the job exists.</p>
          <div style={styles.timingGrid}>
            <div style={styles.timingCard}>
              <div style={styles.timingBadge} />
              <div style={styles.timingIcon}>Premium</div>
              <div style={styles.timingValue}>Notified First</div>
              <div style={styles.timingDesc}>30 minutes ahead of Pro members. First to every hot job.</div>
            </div>
            <div style={styles.timingArrow}>&#8594;</div>
            <div style={styles.timingCard}>
              <div style={styles.timingIcon}>Pro</div>
              <div style={styles.timingValue}>Notified Second</div>
              <div style={styles.timingDesc}>Well ahead of free members. Strong competitive advantage.</div>
            </div>
            <div style={styles.timingArrow}>&#8594;</div>
            <div style={styles.timingCard}>
              <div style={styles.timingIcon}>Starter</div>
              <div style={styles.timingValue}>Notified Last</div>
              <div style={styles.timingDesc}>After a significant delay. Best jobs may already have strong quotes.</div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.availSection}>
        <div style={styles.sectionContainer}>
          <div style={styles.availBox}>
            <div style={styles.sectionLabel}>SPOT AVAILABILITY</div>
            <h2 style={{ ...styles.sectionTitle, marginBottom: '8px' }}>Check Open Spots in Your City</h2>
            <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 24px' }}>We limit the number of businesses per tier in each city, so you compete against fewer bidders than on big lead sites.</p>
            <div style={styles.availSelectors}>
              <div style={styles.availField}>
                <label style={styles.availLabel}>Your City</label>
                <select value={userCity} onChange={e => setUserCity(e.target.value)} style={styles.availSelect}>
                  <optgroup label="Baton Rouge launch cities">
                    {BATON_ROUGE_METRO.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                  <optgroup label="Expansion cities">
                    {STATEWIDE_CAP_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                </select>
              </div>
              <div style={styles.availField}>
                <label style={styles.availLabel}>Your Service Category</label>
                <select value={userCategory} onChange={e => setUserCategory(e.target.value)} style={styles.availSelect}>
                  {CATEGORY_GROUPS.map(group => (
                    <optgroup key={group.id} label={group.label}>
                      {group.slugs.map(c => <option key={c} value={c}>{formatCategory(c)}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div style={styles.availGrid}>
              <div style={styles.availTierCard}>
                <div style={styles.availTierLabel}>Starter</div>
                <div style={styles.availTierSub}>Up to 15 per city</div>
                <SpotBadge city={userCity} category={userCategory} tier="starter" />
              </div>
              <div style={{ ...styles.availTierCard, borderColor: '#3b82f6' }}>
                <div style={{ ...styles.availTierLabel, color: '#3b82f6' }}>Pro</div>
                <div style={styles.availTierSub}>Up to 8 per city</div>
                <SpotBadge city={userCity} category={userCategory} tier="pro" />
              </div>
              <div style={{ ...styles.availTierCard, borderColor: '#7c3aed' }}>
                <div style={{ ...styles.availTierLabel, color: '#7c3aed' }}>Premium</div>
                <div style={styles.availTierSub}>Only 3 per city</div>
                <SpotBadge city={userCity} category={userCategory} tier="premium" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section style={styles.pricingSection}>
        <div style={styles.sectionContainer}>
          <div style={styles.plansGrid}>
            <div style={styles.planCard}>
              <div style={styles.planTier}>Starter</div>
              <div style={styles.planPrice}>$0</div>
              <div style={styles.planPeriod}>per month, forever</div>
              <div style={styles.planDesc}>Get started at no cost</div>
              <div style={styles.planFootprint}>2 categories · 3 cities</div>
              <ul style={styles.featureList}>
                <li style={styles.feature}><span style={styles.check}>+</span> Access to all quote requests</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Submit up to 3 quotes/month</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Email notifications</li>
                <li style={styles.featureOff}><span style={styles.ex}>-</span> Notified after significant delay</li>
                <li style={styles.featureOff}><span style={styles.ex}>-</span> SMS notifications</li>
                <li style={styles.featureOff}><span style={styles.ex}>-</span> Unlimited quotes</li>
              </ul>
              <SpotBadge city={userCity} category={userCategory} tier="starter" />
              <div style={{ marginTop: '16px' }}>
                {token ? (
                  <button style={styles.currentBtn} disabled>Current Plan</button>
                ) : (
                  <Link to="/register?type=business" style={styles.starterBtn}>Get Started Free</Link>
                )}
              </div>
            </div>

            <div style={{
              ...styles.planCard,
              ...styles.planCardFeatured,
              ...(isHighlighted('pro') ? { outline: '3px solid #f59e0b', outlineOffset: '4px' } : {}),
            }}>
              <div style={styles.planBadge}>{isHighlighted('pro') ? 'Your Plan' : 'Most Popular'}</div>
              <div style={styles.planTier}>Pro</div>
              <div style={styles.planPriceGroup}>
                <span style={styles.planStrike}>$249</span>
                <span style={styles.planPriceFeatured}>$124.50</span>
              </div>
              <div style={styles.planPeriod}>$124.50/mo for life · (reg. $249/mo)</div>
              <div style={styles.planSavings}>Save $124.50 every month, for life</div>
              <div style={styles.planFootprint}>up to 5 categories · up to 10 launch cities</div>
              <ul style={styles.featureList}>
                <li style={styles.feature}><span style={styles.check}>+</span> Everything in Starter</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Unlimited quotes</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Email notifications</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Notified ahead of Starter members</li>
                <li style={styles.feature}><span style={styles.check}>+</span> 90-minute head start vs Starter</li>
                <li style={styles.featureOff}><span style={styles.ex}>-</span> SMS notifications</li>
              </ul>
              <SpotBadge city={userCity} category={userCategory} tier="pro" />
              <div style={{ marginTop: '16px' }}>
                <button style={loading === 'pro' ? styles.loadingBtn : styles.proBtn} onClick={() => handleUpgrade('pro')} disabled={loading !== null}>
                  {loading === 'pro' ? 'Processing...' : (isHighlighted('pro') ? 'Continue to Pro Checkout' : 'Get Pro — $124.50/mo for Life')}
                </button>
              </div>
            </div>

            <div style={{
              ...styles.planCard,
              ...styles.planCardPremium,
              ...(isHighlighted('premium') ? { outline: '3px solid #f59e0b', outlineOffset: '4px' } : {}),
            }}>
              <div style={styles.planBadgePremium}>{isHighlighted('premium') ? 'Your Plan' : 'Best Value'}</div>
              <div style={styles.planTier}>Premium</div>
              <div style={styles.planPriceGroup}>
                <span style={styles.planStrike}>$399</span>
                <span style={styles.planPricePremium}>$199.50</span>
              </div>
              <div style={styles.planPeriod}>$199.50/mo for life · (reg. $399/mo)</div>
              <div style={styles.planSavingsPremium}>Save $199.50 every month, for life</div>
              <div style={styles.planFootprint}>up to 10 categories · up to 15 launch cities</div>
              <ul style={styles.featureList}>
                <li style={styles.feature}><span style={styles.check}>+</span> Everything in Pro</li>
                <li style={styles.feature}><span style={styles.checkBold}>+</span> <strong>Notified 30 min before Pro</strong></li>
                <li style={styles.feature}><span style={styles.checkBold}>+</span> <strong>First to see every request</strong></li>
                <li style={styles.feature}><span style={styles.check}>+</span> SMS + Email notifications</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Unlimited quotes</li>
                <li style={styles.feature}><span style={styles.check}>+</span> Priority support</li>
              </ul>
              <SpotBadge city={userCity} category={userCategory} tier="premium" />
              <div style={{ marginTop: '16px' }}>
                <button style={loading === 'premium' ? styles.loadingBtn : styles.premiumBtn} onClick={() => handleUpgrade('premium')} disabled={loading !== null}>
                  {loading === 'premium' ? 'Processing...' : (isHighlighted('premium') ? 'Continue to Premium Checkout' : 'Get Premium — $199.50/mo for Life')}
                </button>
              </div>
            </div>
          </div>
          <p style={styles.planFootnote}>Larger multi-category or multi-city footprints may be reviewed separately.</p>
          <p style={styles.foundingLine}>Founding Members lock in 50% off their paid plan for life.</p>
          <p style={styles.disclaimer}>Founding Member pricing for the first 24 paid businesses on QuoteBid. Discount applies for the life of your active subscription. Canceling forfeits the Founding Member discount.</p>
        </div>
      </section>

      <section style={styles.compareSection}>
        <div style={styles.sectionContainer}>
          <div style={styles.sectionLabel}>FULL COMPARISON</div>
          <h2 style={styles.sectionTitle}>Everything Side by Side</h2>
          <div style={styles.compareTable}>
            <div style={styles.compareHeader}>
              <div style={styles.compareFeatureHeader}>Feature</div>
              <div style={styles.compareColHeader}>Starter</div>
              <div style={{ ...styles.compareColHeader, color: '#10b981' }}>Pro</div>
              <div style={styles.compareColHeader}>Premium</div>
            </div>
            {[
              ['Monthly price', 'Free', '$249/mo', '$399/mo'],
              ['Service footprint', '2 categories · 3 cities', 'up to 5 categories · up to 10 cities', 'up to 10 categories · up to 15 cities'],
              ['Quote submissions', '3/month', 'Unlimited', 'Unlimited'],
              ['Spots per city', '15 max', '8 max', '3 max'],
              ['Notification timing', 'Last (delayed)', 'Second', 'First'],
              ['Head start over Pro', 'None', 'None', '30 minutes'],
              ['Email notifications', 'yes', 'yes', 'yes'],
              ['SMS notifications', 'no', 'no', 'yes'],
              ['Priority support', 'no', 'no', 'yes'],
            ].map(([feature, starter, pro, premium], i) => (
              <div key={i} style={{ ...styles.compareRow, background: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                <div style={styles.compareFeature}>{feature}</div>
                <div style={styles.compareCol}>{starter === 'yes' ? <Check /> : starter === 'no' ? <Cross /> : starter}</div>
                <div style={{ ...styles.compareCol, ...(pro === 'yes' || (pro !== 'no' && pro !== 'None' && pro !== 'Last (delayed)') ? { color: '#10b981', fontWeight: 700 } : {}) }}>
                  {pro === 'yes' ? <Check /> : pro === 'no' ? <Cross /> : pro}
                </div>
                <div style={styles.compareCol}>{premium === 'yes' ? <Check /> : premium === 'no' ? <Cross /> : premium}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.finalCta}>
        <div style={styles.finalCtaContent}>
          <h2 style={styles.finalCtaTitle}>Ready to Win More Jobs?</h2>
          <p style={styles.finalCtaSubtitle}>Founding Member pricing — first 24 paid Founding Members only.</p>
          <div style={styles.finalCtaBtns}>
            <button style={styles.finalCtaPrimary} onClick={() => handleUpgrade('pro')}>
              {loading === 'pro' ? 'Processing...' : 'Get Pro — $124.50'}
            </button>
            <button style={styles.finalCtaSecondary} onClick={() => handleUpgrade('premium')}>
              {loading === 'premium' ? 'Processing...' : 'Get Premium — $199.50'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  root: { fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif", color: '#0f172a' },
  checkoutPlanNotice: { maxWidth: '720px', margin: '20px auto 0', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '14px 18px', borderRadius: '10px', fontSize: '14px', lineHeight: 1.5 },
  checkoutLaneNotice: { maxWidth: '720px', margin: '20px auto 0', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '14px 18px', borderRadius: '10px', fontSize: '14px' },
  checkoutLaneTitle: { fontWeight: 600, marginBottom: '6px' },
  checkoutLaneList: { margin: 0, paddingLeft: '18px' },
  checkoutLaneItem: { marginBottom: '4px', lineHeight: 1.4 },
  hero: { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f4c75 100%)', padding: '80px 24px', textAlign: 'center' as const },
  heroBadge: { display: 'inline-block', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', padding: '8px 20px', borderRadius: '100px', fontSize: '13px', fontWeight: 600, marginBottom: '28px', letterSpacing: '0.02em' },
  heroTitle: { fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: '20px', letterSpacing: '-0.02em' },
  heroAccent: { background: 'linear-gradient(135deg, #3b82f6, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  heroSubtitle: { fontSize: '18px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 },
  timingSection: { padding: '80px 24px', background: '#ffffff' },
  sectionContainer: { maxWidth: '1100px', margin: '0 auto' },
  sectionLabel: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', color: '#3b82f6', textTransform: 'uppercase' as const, marginBottom: '12px' },
  sectionTitle: { fontSize: 'clamp(24px, 3vw, 40px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '16px' },
  timingSubtitle: { fontSize: '16px', color: '#64748b', lineHeight: 1.6, marginBottom: '48px', maxWidth: '600px' },
  timingGrid: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' as const },
  timingCard: { flex: 1, minWidth: '200px', padding: '32px 24px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', position: 'relative' as const },
  timingBadge: { position: 'absolute' as const, top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '16px 16px 0 0' },
  timingArrow: { fontSize: '24px', color: '#cbd5e1', flexShrink: 0 },
  timingIcon: { fontSize: '16px', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase' as const, letterSpacing: '0.1em', marginBottom: '8px' },
  timingValue: { fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' },
  timingDesc: { fontSize: '14px', color: '#64748b', lineHeight: 1.5 },
  availSection: { padding: '60px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' },
  availBox: { background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', padding: '40px', boxShadow: '0 4px 24px rgba(0,0,0,0.04)' },
  availSelectors: { display: 'flex', gap: '16px', flexWrap: 'wrap' as const, marginBottom: '28px' },
  availField: { flex: 1, minWidth: '200px' },
  availLabel: { display: 'block', fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: '6px' },
  availSelect: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#0f172a', background: '#f8fafc', cursor: 'pointer', outline: 'none' },
  availGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
  availTierCard: { padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc' },
  availTierLabel: { fontSize: '15px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' },
  availTierSub: { fontSize: '12px', color: '#94a3b8', marginBottom: '10px' },
  pricingSection: { padding: '80px 24px', background: '#f8fafc' },
  plansGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', alignItems: 'start', marginBottom: '40px' },
  planCard: { background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', padding: '36px 28px', position: 'relative' as const },
  planCardFeatured: { border: '2px solid #3b82f6', boxShadow: '0 8px 32px rgba(59,130,246,0.15)', transform: 'scale(1.03)' },
  planCardPremium: { border: '2px solid #0f172a', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' },
  planBadge: { position: 'absolute' as const, top: '-14px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '4px 20px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' as const },
  planBadgePremium: { position: 'absolute' as const, top: '-14px', left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: '#fff', padding: '4px 20px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' as const },
  planTier: { fontSize: '13px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.1em', marginBottom: '12px' },
  planPrice: { fontSize: '52px', fontWeight: 800, color: '#0f172a', lineHeight: 1, marginBottom: '4px' },
  planPriceGroup: { display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' },
  planStrike: { fontSize: '20px', color: '#cbd5e1', textDecoration: 'line-through' },
  planPriceFeatured: { fontSize: '52px', fontWeight: 800, color: '#0f172a', lineHeight: 1 },
  planPricePremium: { fontSize: '52px', fontWeight: 800, color: '#0f172a', lineHeight: 1 },
  planPeriod: { fontSize: '13px', color: '#94a3b8', marginBottom: '8px' },
  planDesc: { fontSize: '13px', color: '#64748b', marginBottom: '16px' },
  planSavings: { display: 'inline-block', background: '#eff6ff', color: '#2563eb', fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '100px', marginBottom: '24px' },
  planSavingsPremium: { display: 'inline-block', background: '#f0fdf4', color: '#16a34a', fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '100px', marginBottom: '24px' },
  featureList: { listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column' as const, gap: '10px' },
  feature: { fontSize: '14px', color: '#374151', display: 'flex', alignItems: 'flex-start', gap: '8px' },
  featureOff: { fontSize: '14px', color: '#cbd5e1', display: 'flex', alignItems: 'flex-start', gap: '8px' },
  check: { color: '#10b981', fontWeight: 700, flexShrink: 0 },
  checkBold: { color: '#10b981', fontWeight: 700, flexShrink: 0, fontSize: '16px' },
  ex: { color: '#e2e8f0', flexShrink: 0 },
  currentBtn: { width: '100%', padding: '14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', color: '#94a3b8', cursor: 'default' },
  starterBtn: { display: 'block', width: '100%', padding: '14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', fontWeight: 600, color: '#0f172a', textDecoration: 'none', textAlign: 'center' as const, boxSizing: 'border-box' as const },
  proBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg, #3b82f6, #10b981)', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 16px rgba(59,130,246,0.3)' },
  premiumBtn: { width: '100%', padding: '14px', background: '#0f172a', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
  loadingBtn: { width: '100%', padding: '14px', background: '#94a3b8', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, color: '#fff', cursor: 'not-allowed' },
  planFootprint: { fontSize: '13px', fontWeight: 600, color: '#475569', background: '#f1f5f9', borderRadius: '8px', padding: '6px 12px', marginBottom: '20px' },
  planFootnote: { textAlign: 'center' as const, fontSize: '13px', color: '#64748b', marginBottom: '6px' },
  foundingLine: { textAlign: 'center' as const, fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '10px' },
  disclaimer: { textAlign: 'center' as const, fontSize: '13px', color: '#94a3b8' },
  compareSection: { padding: '80px 24px', background: '#ffffff' },
  compareTable: { borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', marginTop: '40px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  compareHeader: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', background: '#1e293b', padding: '16px 24px', gap: '16px' },
  compareRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '14px 24px', gap: '16px', borderTop: '1px solid #e2e8f0' },
  compareFeatureHeader: { fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.7)' },
  compareFeature: { fontSize: '14px', fontWeight: 600, color: '#0f172a' },
  compareColHeader: { textAlign: 'center' as const, fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 },
  compareCol: { textAlign: 'center' as const, fontSize: '14px', color: '#0f172a', fontWeight: 500 },
  finalCta: { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '100px 24px', textAlign: 'center' as const },
  finalCtaContent: { maxWidth: '600px', margin: '0 auto' },
  finalCtaTitle: { fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, color: '#ffffff', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '16px' },
  finalCtaSubtitle: { fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginBottom: '40px' },
  finalCtaBtns: { display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' as const },
  finalCtaPrimary: { padding: '16px 32px', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(59,130,246,0.4)' },
  finalCtaSecondary: { padding: '16px 32px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', fontSize: '16px', fontWeight: 700, cursor: 'pointer' },
};

export default Pricing;